import { toDollars } from "common/utils/formatting";

import { useClinic } from "client/clinics/hooks";
import ShippingRate from "client/shipping/components/ShippingRate";
import { useShippingRatesSummaryQuery } from "client/shipping/hooks";
import { externalUrls } from "client/routes";
import { useSupportChat } from "client/support/hooks";

import TextButton from "../components/buttons/TextButton";
import DrawerLayout from "../components/stackables/DrawerLayout";
import { useCurrentDrawer } from "../hooks/stackables";

import styles from "../styles/ShippingInfoDrawer.module.less";

const ShippingInfoDrawer = () => {
  const { close } = useCurrentDrawer();

  const clinic = useClinic();
  const chat = useSupportChat();
  const { data: ratesSummary, isLoading, error } = useShippingRatesSummaryQuery();

  const openChat = () => {
    chat.show();
    close();
  };

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (error || !ratesSummary) {
    return <DrawerLayout.FetchError error={error} entityName="shipping rates" isPlural={true} />;
  }

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Our shipping rates & policies" onClose={close} />
      <DrawerLayout.Content>
        {clinic.free_shipping_over && (
          <div className={styles.banner}>
            <div className={styles.label}>Free shipping</div>
            <div className={styles.content}>
              Get free standard shipping on orders over{" "}
              {toDollars(clinic.free_shipping_over, { roundCents: true })}
            </div>
          </div>
        )}
        <div className={styles.section}>
          <ShippingRate
            shippingRates={ratesSummary.primary}
            title="Primary pharmacy shipping rates"
          />
          {ratesSummary.diet && (
            <p className={styles.body}>
              All diet items (Hill&apos;s Pet Nutrition, Royal Canin Veterinary Diets, and Purina)
              ship directly from the manufacturer for{" "}
              {ratesSummary.diet.is_eligible_for_free_over ? (
                <>
                  FREE for orders over {toDollars(ratesSummary.diet.free_shipping_over)} and{" "}
                  {toDollars(ratesSummary.diet.cost)} for orders under{" "}
                  {toDollars(ratesSummary.diet.free_shipping_over)}
                </>
              ) : (
                <>{toDollars(ratesSummary.diet.cost)}</>
              )}
              . All other prescription and non-prescription products are shipped by your{" "}
              veterinarian&apos;s pharmacy partner.
            </p>
          )}
        </div>
        <div className={styles.section}>
          <ShippingRate
            shippingRates={ratesSummary?.compound}
            title="Compound pharmacy shipping rates"
          />
          <p className={styles.body}>
            Prices above reflect shipping rates for most compounded medications, shipped from{" "}
            {ratesSummary.compound_vendor_name}. Shipping rates may differ for compounded
            medications shipped from other pharmacy partners and will be reflected in cart.
          </p>
          {ratesSummary.has_free_compound_autoship_shipping && (
            <p className={styles.body}>
              Orders containing an Autoship item qualify for free standard shipping from{" "}
              {ratesSummary.compound_vendor_name}, regardless of order total.
            </p>
          )}
        </div>
        <div className={styles.section}>
          <h3 className={styles.header}>Order approvals &amp; shipping</h3>
          <ul className={styles.body}>
            <li>
              Orders containing non-prescription items or items that have an approved prescription
              on file will be processed immediately.
            </li>
            <li>
              Your veterinarian will be contacted for orders that contain items requiring
              prescription approval. Your order will ship following your veterinarian&apos;s
              approval.
            </li>
            <li>
              You will see a hold on your card for the value of the order. Credit cards will not be
              charged until the time of shipment.
            </li>
            <li>Shipping rates are subject to change at any time.</li>
          </ul>
        </div>
        <div className={styles.section}>
          <h3 className={styles.header}>Temperature sensitive items</h3>
          <ul className={styles.body}>
            <li>
              Temperature sensitive medications must ship via FedEx Priority Overnight shipping (1-2
              business days).
            </li>
            <li>Time and temperature-sensitive products will ship Monday through Thursday.</li>
            <li>All autoship orders are subject to the above conditions.</li>
          </ul>
        </div>
        <div className={styles.section}>
          <h3 className={styles.header}>Additional state restrictions</h3>
          <p className={styles.body}>
            Additional state restrictions for specific products are included in the product
            information on the site. You may be restricted from purchasing certain items based on
            local regulations.
          </p>
        </div>
        <div className={styles.section}>
          <h3 className={styles.header}>Returns &amp; refunds (prescription &amp; OTC)</h3>
          <p className={styles.body}>
            Your veterinary clinic&apos;s pharmacy partners are unable to take returns of
            prescription products due to state and federal laws prohibiting pharmacies from
            reselling prescription products. Please visit the{" "}
            <TextButton hasUnderline={true} href={externalUrls.disposalOfUnusedMedicine()}>
              following link
            </TextButton>{" "}
            to learn how to properly dispose of your unused prescription medication.
          </p>
          <p className={styles.body}>
            Of course, if you&apos;re unhappy in any way with your purchase of either prescription
            or nonprescription products, please reach out to customer support using the{" "}
            <TextButton onClick={openChat} hasUnderline={true}>
              live chat
            </TextButton>{" "}
            on our site. Refunds and replacements may be issued on a case-by-case basis. If eligible
            for a refund or replacement, your request will typically be processed within 1 business
            day. The time it takes for a refund to reflect on your credit card statement is
            determined by your financial institution and typically takes 5-10 business days
          </p>
        </div>
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export default ShippingInfoDrawer;
